import { createFileRoute, redirect } from '@tanstack/react-router'

import 'ol/ol.css'

import { postLoginRedirect } from '@/shared/auth/post-login-redirect'
import { useAuthStore } from '@/shared/auth/stores/authStore'
import {
  GIS_PERIODS,
  GIS_TABS,
  GisViewPage,
  gisEntitiesQueryOptions,
  gisIssuesQueryOptions,
  gisMandalsQueryOptions,
  gisSitesQueryOptions,
  gisTrendsQueryOptions,
  type GisPeriod,
  type GisTab,
} from '@/systems/monitoring'

interface GisSearch {
  tab?: GisTab | undefined
  period?: GisPeriod | undefined
  entityId?: string | undefined
  mandal?: string | undefined
  dateFrom?: string | undefined
  dateTo?: string | undefined
}

function parseTab(v: unknown): GisTab | undefined {
  return typeof v === 'string' && (GIS_TABS as readonly string[]).includes(v) ? (v as GisTab) : undefined
}
function parsePeriod(v: unknown): GisPeriod | undefined {
  return typeof v === 'string' && (GIS_PERIODS as readonly string[]).includes(v) ? (v as GisPeriod) : undefined
}
function parseEntityId(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined
}
function parseMandal(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined
}
function parseDate(v: unknown): string | undefined {
  return typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : undefined
}

export const Route = createFileRoute('/gis')({
  beforeLoad: () => {
    const state = useAuthStore.getState()
    if (!state.isAuthenticated) {
      throw redirect({ to: '/login' })
    }
    const role = state.user?.role_name
    if (role !== 'super_admin' && role !== 'admin') {
      throw redirect({ to: postLoginRedirect(role) })
    }
  },
  validateSearch: (search: Record<string, unknown>): GisSearch => {
    const period = parsePeriod(search['period'])
    const dateFrom = parseDate(search['dateFrom'])
    const dateTo = parseDate(search['dateTo'])
    // `custom` is only valid with a complete, ordered range; otherwise drop period + dates → falls back to 30d.
    const customOk = period === 'custom' && !!dateFrom && !!dateTo && dateFrom <= dateTo
    return {
      tab: parseTab(search['tab']),
      period: period === 'custom' && !customOk ? undefined : period,
      entityId: parseEntityId(search['entityId']),
      mandal: parseMandal(search['mandal']),
      dateFrom: customOk ? dateFrom : undefined,
      dateTo: customOk ? dateTo : undefined,
    }
  },
  loaderDeps: ({ search }) => ({
    period: search.period ?? ('30d' as GisPeriod),
    entityId: search.entityId,
    dateFrom: search.dateFrom,
    dateTo: search.dateTo,
  }),
  loader: ({ context: { queryClient }, deps }) => {
    const scope = { period: deps.period, entityId: deps.entityId, dateFrom: deps.dateFrom, dateTo: deps.dateTo }
    void queryClient.prefetchQuery(gisMandalsQueryOptions(scope))
    void queryClient.prefetchQuery(gisSitesQueryOptions(scope))
    void queryClient.prefetchQuery(gisTrendsQueryOptions(scope))
    void queryClient.prefetchQuery(gisEntitiesQueryOptions())
    void queryClient.prefetchQuery(gisIssuesQueryOptions(scope))
  },
  component: GisViewPage,
})
